/*
 * gift-by-text.js - "Send Flowers as a Text" (Option C, single order)
 * ---------------------------------------------------------------------------
 * Turns the bouquet in the cart into a NON-SHIPPABLE gift purchase so Shopify's
 * checkout skips the address step (the recipient adds their address later via a
 * texted link). No address is collected from the gifter at checkout.
 *
 * The swap: add the non-ship proxy lines first, then remove the real (shippable) bouquet
 *   1) the matching non-ship "Send by Text Gift" ladder variant (same price),
 *   2) the flat non-ship "Gift Shipping & Handling" product, and
 *   3) the "Gift Estimated Tax" proxy (priced by the gift-cart-transform Function),
 * carrying the real bouquet's identity + the gift details as line properties.
 * Then send the buyer to /checkout.
 *
 * CONFIG (injected by Liquid - see snippets/gift-by-text-config.liquid):
 *   window.giftByText = {
 *     ladder:       { "1295": 40000000001, "5000": 40000000002, ... }, // price(cents) -> ladder variant id
 *     shVariantId:  40000000099,                                       // "Gift Shipping & Handling" variant id
 *     taxVariantId: 40000000100,                                       // "Gift Estimated Tax" variant id (optional)
 *     apiBase:      "https://…",                                       // backend origin (resume-banner verify)
 *     proxyHandles: ["send-by-text-gift", ...],                        // the non-ship proxy handles
 *     sharePath:    "/pages/gift-share",                               // where the recipient link is shared from
 *   }
 *
 * Line properties written (underscore-prefixed = hidden from checkout; the cart
 * drawer renders _gift_* itself):
 *   _gift_token, _gift_variant_id, _gift_title, _gift_image, _gift_from, _gift_note
 */
(function () {
  "use strict";

  var CFG = window.giftByText || {};

  // ---- session gate: only show the option to visitors who arrived via ?text-to-gift=true ----
  // The flag persists for the whole session (sessionStorage), so it survives normal browsing.
  // Special value ?text-to-gift=desktop also forces the opt-in visible on desktop widths
  // (it's normally mobile-only) so the whole flow can be tested on a computer screen.
  var GATE_KEY = "gbt-enabled";
  var DESKTOP_KEY = "gbt-desktop"; // override the mobile-only display gate for testing
  function isEnabled() {
    try {
      var p = new URLSearchParams(window.location.search);
      if (p.has("text-to-gift")) {
        var v = p.get("text-to-gift");
        if (v === "desktop") {
          sessionStorage.setItem(GATE_KEY, "1");
          sessionStorage.setItem(DESKTOP_KEY, "1");
        } else if (v === null || v === "" || v === "true" || v === "1") {
          sessionStorage.setItem(GATE_KEY, "1");
        } else if (v === "false" || v === "0") {
          sessionStorage.removeItem(GATE_KEY);
          sessionStorage.removeItem(DESKTOP_KEY);
        }
      }
      return sessionStorage.getItem(GATE_KEY) === "1";
    } catch (e) {
      return false;
    }
  }
  function isDesktopForced() {
    try {
      return sessionStorage.getItem(DESKTOP_KEY) === "1";
    } catch (e) {
      return false;
    }
  }
  // Who may SEE the cart entry point: "off" | "gift_url" (default) | "everyone". Merchant-controlled via
  // the gbt_entrypoint_mode theme setting. Deliberately separate from the session gate above: at launch
  // the option is gift-link-only, and it can later be opened to all traffic without any code change.
  function entryMode() {
    var m = CFG.entrypointMode;
    return m === "off" || m === "everyone" ? m : "gift_url";
  }

  // Enter a gift SESSION. Distinct from merely seeing the entry point: gbt-enabled also hides the
  // card-customizer cart upsell and suppresses the GWP freebie, so under "everyone" mode it must wait
  // until the shopper actually opens the send-by-text form (texting intent) rather than applying to
  // every shopper who can see the option.
  function enterGiftMode() {
    try {
      sessionStorage.setItem(GATE_KEY, "1");
    } catch (e) {}
    document.documentElement.classList.add("gbt-enabled");
  }

  function applyGate() {
    var mode = entryMode();
    // "off" is fully dark for NEW sessions: no entry point and no gift session either. Without the early
    // return a stale/shared ?text-to-gift=true link would still set gbt-enabled, which suppresses the GWP
    // freebie and hides the card-customizer upsell for that shopper's whole session - real revenue
    // features switched off for someone who cannot create a gift anyway. Redemption is a separate page
    // and is deliberately unaffected, so already-sent links still complete.
    if (mode === "off") return;

    var sessionOn = isEnabled();
    if (sessionOn) document.documentElement.classList.add("gbt-enabled");
    if (isDesktopForced()) document.documentElement.classList.add("gbt-force-desktop");
    if (mode === "everyone" || sessionOn) {
      document.documentElement.classList.add("gbt-entry");
    }
  }
  // Exit gift mode. A shared/bookmarked ?text-to-gift=true URL otherwise keeps the whole SESSION gated
  // (the gift cart form, the demoted checkout) with no way out for a shopper who just wants to buy
  // normally. Clearing the gate + classes returns the storefront to normal.
  function clearGate() {
    try {
      sessionStorage.removeItem(GATE_KEY);
      sessionStorage.removeItem(DESKTOP_KEY);
    } catch (e) {}
    document.documentElement.classList.remove("gbt-enabled", "gbt-active", "gbt-force-desktop");
    // Under "everyone" the entry point is a permanent storefront feature, so exiting a gift session
    // must not hide it. Under gift_url it was only visible because of the session, so it goes too.
    if (entryMode() !== "everyone") document.documentElement.classList.remove("gbt-entry");
  }

  // ---- "unsent gift" recovery cookie ----
  // Set at send time (just before /checkout). If the gifter bails on the thank-you page (taps "Continue
  // shopping" instead of the extension's "Continue"), the paid order exists but the recipient link was
  // never shared. This first-party cookie lets the storefront nudge them back to finish. 7-day life =
  // the claim window. Same-browser only (a device switch loses it - the backend reminder is the wider
  // net). Cleared when they reach the share page or dismiss the banner.
  var PENDING_COOKIE = "gbt_pending";
  function setPendingCookie(tok, from) {
    try {
      var val = encodeURIComponent(JSON.stringify({ t: tok, from: from || "" }));
      document.cookie =
        PENDING_COOKIE + "=" + val + ";path=/;max-age=" + 60 * 60 * 24 * 7 + ";samesite=lax";
    } catch (e) {}
  }
  function readPendingCookie() {
    try {
      var m = document.cookie.match(/(?:^|;\s*)gbt_pending=([^;]+)/);
      if (!m) return null;
      var obj = JSON.parse(decodeURIComponent(m[1]));
      return obj && obj.t ? obj : null;
    } catch (e) {
      return null;
    }
  }
  function clearPendingCookie() {
    try {
      document.cookie = PENDING_COOKIE + "=;path=/;max-age=0;samesite=lax";
    } catch (e) {}
  }

  function getCart() {
    return fetch("/cart.js", { headers: { Accept: "application/json" } }).then(function (r) {
      return r.json();
    });
  }

  // The gift catalog products (ladder variants + S&H) should never themselves be
  // treated as the "bouquet"; identify them by their known ids from config.
  function isGiftCatalogItem(item) {
    if (item.id === CFG.shVariantId) return true;
    if (CFG.ladder) {
      for (var k in CFG.ladder) {
        if (Object.prototype.hasOwnProperty.call(CFG.ladder, k) && CFG.ladder[k] === item.id) return true;
      }
    }
    return false;
  }

  function prop(item, name) {
    return item && item.properties ? item.properties[name] : undefined;
  }

  // A personalized greeting-card line added by the card customizer. IMPORTANT:
  // the card LINE has product_type "greeting_card" AND a _card_customizer_uuid,
  // but the *parent* bouquet line carries the SAME uuid while keeping its own
  // product_type (see custom-card-customizer.js: parent = uuid match with
  // product_type !== "greeting_card"). So the card line must be identified by
  // product_type - matching on the uuid alone would wrongly catch the bouquet we
  // actually want to gift.
  function isGreetingCardLine(item) {
    return (item.product_type || "").toLowerCase() === "greeting_card";
  }

  // Any card-customizer activity in the cart: a card line, OR any line still
  // stamped with a _card_customizer_uuid. Used only to WARN the gifter (their
  // card won't ride along) - not to pick the bouquet.
  function hasCardCustomizer(cart) {
    return (cart.items || []).some(function (i) {
      return isGreetingCardLine(i) || prop(i, "_card_customizer_uuid");
    });
  }

  // Digital gift cards can't ship, so they can never be gifted by text. Shopify's
  // AJAX cart flags them with item.gift_card === true; fall back to product_type /
  // handle "gift-card".
  function isGiftCardLine(item) {
    if (item.gift_card === true) return true;
    var type = (item.product_type || "").toLowerCase().replace(/[\s_]/g, "-");
    if (type === "gift-card") return true;
    if ((item.handle || "").toLowerCase() === "gift-card") return true;
    return false;
  }
  function hasGiftCard(cart) {
    return (cart.items || []).some(isGiftCardLine);
  }

  // Lines that must never be swapped into a text gift, i.e. never picked as the
  // bouquet: our own gift-catalog proxies, personalized greeting-card lines, and
  // digital gift cards.
  // A Gift-With-Purchase freebie the GWP app auto-added (line property _Gifted:GWP). Never gift it -
  // a lingering GWP gift would otherwise be picked as "the bouquet" (wrong item, or NO_LADDER_POINT).
  function isGwpGiftLine(item) {
    return !!(item && item.properties && item.properties._Gifted === "GWP");
  }
  function isExcludedLine(item) {
    return (
      isGiftCatalogItem(item) ||
      isGreetingCardLine(item) ||
      isGiftCardLine(item) ||
      isGwpGiftLine(item)
    );
  }

  // Pick the bouquet to gift. v1 scope: a single bouquet. If the cart holds more
  // than one giftable line we take the first and surface a note to the caller.
  function pickBouquet(cart) {
    var items = (cart.items || []).filter(function (i) {
      return !isExcludedLine(i);
    });
    return { bouquet: items[0] || null, extraCount: Math.max(0, items.length - 1) };
  }

  function ladderVariantForPrice(cents) {
    var key = String(cents);
    return CFG.ladder && CFG.ladder[key] ? CFG.ladder[key] : null;
  }

  function post(url, body) {
    return fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify(body),
    }).then(function (r) {
      return r.json().then(function (data) {
        if (!r.ok) { var err = new Error(data.description || "Cart error"); err.data = data; throw err; }
        return data;
      });
    });
  }

  function token() {
    if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID();
    // fallback for older browsers
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
      var r = (Math.random() * 16) | 0;
      return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
    });
  }

  /**
   * Convert the current cart into a "send by text" gift and go to checkout.
   * @param {{from: string, note: string}} details
   * @returns {Promise<{token:string}>}
   */
  function sendByText(details) {
    details = details || {};
    if (!CFG.ladder || !CFG.shVariantId) {
      return Promise.reject(new Error("gift-by-text: missing config (ladder / shVariantId)"));
    }
    return getCart().then(function (cart) {
      var picked = pickBouquet(cart);
      var bouquet = picked.bouquet;
      if (!bouquet) throw new Error("No bouquet in cart to send.");

      var perUnitCents = bouquet.price; // Shopify line price is per-unit in cents
      var ladderId = ladderVariantForPrice(perUnitCents);
      if (!ladderId) {
        // No matching price point - the ladder needs this price added.
        var ladderErr = new Error("No gift price point for " + perUnitCents + "¢");
        ladderErr.code = "NO_LADDER_POINT";
        ladderErr.cents = perUnitCents;
        throw ladderErr;
      }

      var t = token();
      var props = {
        _gift_token: t,
        _gift_variant_id: String(bouquet.variant_id || bouquet.id),
        _gift_title: bouquet.product_title || bouquet.title,
        _gift_image: bouquet.image || (bouquet.featured_image && bouquet.featured_image.url) || "",
        _gift_from: details.from || "",
        _gift_note: details.note || "",
      };

      // Gift the full quantity of the chosen bouquet (sum any duplicate lines of it).
      var giftVariantId = bouquet.variant_id || bouquet.id;
      var qty =
        (cart.items || []).reduce(function (sum, i) {
          return !isExcludedLine(i) && (i.variant_id || i.id) === giftVariantId
            ? sum + i.quantity
            : sum;
        }, 0) || bouquet.quantity;

      // Capture the original (real) cart line keys up front so we can remove them ONLY AFTER the
      // proxies are successfully added. A failed add (stock-out / throttle / network) must never leave
      // the shopper with an empty cart - so build the replacement first, then clear the originals.
      var originalKeys = (cart.items || []).map(function (i) { return i.key; });

      // The non-ship proxy lines: gift ladder (matching bouquet price) + flat shipping + the
      // estimated-tax hold line (priced by gift-cart-transform to 15% of bouquet+shipping). The tax
      // line is only added when its catalog product exists (taxVariantId present in config).
      var items = [
        { id: ladderId, quantity: qty, properties: props },
        { id: CFG.shVariantId, quantity: 1 },
      ];
      if (CFG.taxVariantId) items.push({ id: CFG.taxVariantId, quantity: 1 });

      // 1) ADD the proxies first. If this rejects (post() throws on !ok), the original cart is
      //    untouched and the .catch surfaces the error - no destructive clear happened.
      return post("/cart/add.js", { items: items })
        .then(function () {
          // 2) proxies are in - now remove every original shippable line (the bouquet, extra bouquets,
          //    add-on cards, any GWP freebie) so nothing shippable survives to re-add the address step,
          //    and stamp the cart attributes the thank-you extension reads. One request.
          var payload = {
            attributes: {
              gbt_token: t,
              gbt_from: details.from || "",
              gbt_title: props._gift_title || "",
              gbt_image: props._gift_image || "",
              gbt_note: details.note || "",
            },
          };
          if (originalKeys.length) {
            payload.updates = {};
            originalKeys.forEach(function (k) { payload.updates[k] = 0; });
          }
          return post("/cart/update.js", payload);
        })
        .then(function () {
          // Same-session recovery net for a gifter who abandons the thank-you "send the text" step.
          setPendingCookie(t, details.from || "");
          window.location.href = "/checkout";
          return { token: t };
        });
    });
  }

  // Inspect the live cart when the opt-in opens: warn if a personalized card is
  // present (it won't ride along on the text gift), and BLOCK if the only giftable
  // thing in the cart is a gift card (gift cards can't ship, so can't be texted).
  function refreshOptinState(root) {
    var warn = root.querySelector("[data-gbt-warn]");
    var submit = root.querySelector("[data-gbt-submit]");
    getCart()
      .then(function (cart) {
        var picked = pickBouquet(cart);
        var msgs = [];
        var block = false;

        if (!picked.bouquet && hasGiftCard(cart)) {
          // Nothing giftable in the cart except a gift card - don't offer to proceed.
          block = true;
          msgs.push("Gift cards can't be sent by text. Add a bouquet to send a gift.");
        } else {
          if (hasCardCustomizer(cart)) {
            msgs.push("Personalized cards aren't included with text gifts yet, so your card won't be added.");
          }
          if (hasGiftCard(cart)) {
            msgs.push("Gift cards can't be sent by text and won't be included.");
          }
          // v1 sends a single bouquet. If the cart holds more than one giftable bouquet, the extras are
          // dropped on send (sendByText picks the first) - tell the gifter so they don't silently lose an item.
          if (picked.extraCount > 0) {
            msgs.push(
              picked.extraCount === 1
                ? "Only one bouquet can be sent per text gift, so the other bouquet in your cart won't be included."
                : "Only one bouquet can be sent per text gift, so the other " +
                    picked.extraCount +
                    " bouquets in your cart won't be included."
            );
          }
        }

        if (submit) submit.disabled = block;
        if (warn) {
          warn.hidden = msgs.length === 0;
          warn.textContent = msgs.join(" ");
        }
      })
      .catch(function () {
        /* non-fatal: leave the opt-in in its default state */
      });
  }

  // ---- cart-drawer opt-in wiring (delegated, survives cart re-renders) ----
  function initOptin() {
    if (window.__gbtOptinBound) return;
    window.__gbtOptinBound = true;
    document.addEventListener("click", function (e) {
      // "Check out normally with an address" (the demoted checkout in an active gift session) means the
      // shopper isn't sending a gift - exit gift mode so the rest of their session is normal. Do NOT
      // preventDefault: this is the native checkout button, let it submit.
      if (
        document.documentElement.classList.contains("gbt-active") &&
        e.target.closest(".cart__checkout")
      ) {
        clearGate();
      }
      var toggle = e.target.closest("[data-gbt-toggle]");
      if (toggle) {
        var root = toggle.closest("[data-gbt-optin]");
        if (!root) return; // not the cart opt-in widget - ignore
        var form = root.querySelector("[data-gbt-form]");
        if (form) {
          form.hidden = !form.hidden;
          // gbt-active = the shopper opened the send-by-text form (texting intent). The primary-CTA
          // swap (green button, demoted checkout) is gated on this, so it only kicks in on intent.
          document.documentElement.classList.toggle("gbt-active", !form.hidden);
          // Opening the form IS the intent, so this is where an "everyone" shopper joins the gift
          // session. Under gift_url they are already in one and this is a no-op. The CTA swap is
          // gated on .gbt-enabled.gbt-active, so without this it would never fire in everyone mode.
          if (!form.hidden) enterGiftMode();
          if (!form.hidden) refreshOptinState(root); // opening: check for cards / gift cards
        }
        return;
      }
      var submit = e.target.closest("[data-gbt-submit]");
      if (submit) {
        var r = submit.closest("[data-gbt-optin]");
        if (!r) return; // this module loads site-wide; only handle submits inside the cart opt-in
        e.preventDefault();
        var err = r.querySelector("[data-gbt-err]");
        var fromEl = r.querySelector("[data-gbt-from]");
        var noteEl = r.querySelector("[data-gbt-note]");
        var details = { from: fromEl ? fromEl.value.trim() : "", note: noteEl ? noteEl.value.trim() : "" };
        if (err) err.hidden = true;
        submit.disabled = true;
        var orig = submit.textContent;
        submit.textContent = "One sec…";
        sendByText(details).catch(function (ex) {
          submit.disabled = false;
          submit.textContent = orig;
          if (err) {
            err.hidden = false;
            err.textContent =
              ex && ex.code === "NO_LADDER_POINT"
                ? "This bouquet isn't available as a text gift yet."
                : (ex && ex.message) || "Something went wrong.";
          }
        });
      }
    });

    // Re-check the warning/block state whenever the cart's contents actually change (add/remove/qty,
    // incl. the card customizer's own "remove card" flow) while the opt-in form is already open. The
    // MutationObserver in watchForOptin only re-opens the form when the WHOLE [data-gbt-optin] widget
    // node is replaced (e.g. the drawer's initial render) - it does not fire for cart.js updates, which
    // only patch the line-items sub-tree the widget lives outside of. Without this, deleting a
    // personalized card (or a gift card, or an extra bouquet) leaves the warning text stale until the
    // shopper closes and reopens the form. theme:cart:change fires on every such cart.js refresh
    // (theme.dev.js CartItems.build).
    document.addEventListener("theme:cart:change", function () {
      var roots = document.querySelectorAll("[data-gbt-optin]");
      for (var i = 0; i < roots.length; i++) {
        var root = roots[i];
        var form = root.querySelector("[data-gbt-form]");
        if (form && !form.hidden) refreshOptinState(root);
      }
    });

    // The opt-in inputs live inside the cart's <form id="cartForm">, whose default submit is the
    // Checkout button. Pressing Enter in the name/note fields would otherwise submit that form and
    // check out the REAL shippable bouquet (skipping the gift swap entirely). Intercept Enter: block
    // the native submit and route it to "Continue - send by text" instead.
    document.addEventListener("keydown", function (e) {
      if (e.key !== "Enter" && e.keyCode !== 13) return;
      var field = e.target.closest && e.target.closest("[data-gbt-from], [data-gbt-note]");
      if (!field) return;
      var root = field.closest("[data-gbt-optin]");
      if (!root) return;
      e.preventDefault();
      var submit = root.querySelector("[data-gbt-submit]");
      if (submit && !submit.disabled) submit.click();
    });
  }
  // Auto-open the opt-in form for gift-URL sessions: arriving via ?text-to-gift=true IS the texting
  // intent, so the send-by-text form should be open and primary (green CTA) without an extra click.
  function openOptin(root) {
    if (!root) return;
    var form = root.querySelector("[data-gbt-form]");
    if (form && form.hidden) {
      form.hidden = false;
      document.documentElement.classList.add("gbt-active");
      refreshOptinState(root); // check for cards / gift cards
    }
  }
  function autoOpenAll() {
    // Respect the entry-point mode first. isEnabled() has a side effect (it records the URL param into
    // sessionStorage), so calling it in "off" mode would auto-open a hidden form and stamp gbt-active on
    // <html> for a shopper who can't send a gift. Inert today (the CTA swap needs .gbt-enabled AND
    // .gbt-active) but it is state nobody asked for.
    if (entryMode() === "off") return;
    if (!isEnabled()) return;
    var roots = document.querySelectorAll("[data-gbt-optin]");
    for (var i = 0; i < roots.length; i++) openOptin(roots[i]);
  }
  // The cart drawer re-renders its contents (on open / after cart updates), which resets the form to
  // hidden - so watch for the opt-in re-appearing and re-open it each time.
  function watchForOptin() {
    if (!isEnabled() || !window.MutationObserver) return;
    autoOpenAll();
    new MutationObserver(function (muts) {
      for (var i = 0; i < muts.length; i++) {
        var added = muts[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var n = added[j];
          if (n.nodeType === 1 && ((n.matches && n.matches("[data-gbt-optin]")) ||
              (n.querySelector && n.querySelector("[data-gbt-optin]")))) {
            autoOpenAll();
            return;
          }
        }
      }
    }).observe(document.body, { childList: true, subtree: true });
  }

  // Clean up STRAY gift-proxy lines left in the cart by an abandoned funding checkout. The proxies
  // (the ladder / shipping / tax placeholders) are only valid during the funding checkout - the cart
  // hides them from its product list but still counts them toward the item count + subtotal, so a
  // leftover shows up as a phantom item (e.g. a $69 "placeholder" with no remove button). Theme JS
  // never runs on the Shopify checkout, so if these lines are present on a storefront page they are
  // strays and we remove them.
  // Single source of truth is CFG.proxyHandles (from gift-by-text-config.liquid); fall back to the
  // known handles if config is unavailable.
  var GBT_PROXY_HANDLES = (function () {
    var list =
      CFG.proxyHandles && CFG.proxyHandles.length
        ? CFG.proxyHandles
        : ["send-by-text-gift", "gift-shipping-handling", "gift-estimated-tax"];
    var map = {};
    for (var i = 0; i < list.length; i++) map[list[i]] = 1;
    return map;
  })();
  var gbtCleanupReloaded = false; // in-memory reload guard (survives a throwing/blocked sessionStorage)
  function cleanStrayProxies() {
    fetch("/cart.js", { headers: { Accept: "application/json" } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        // Remove ONLY the proxy lines (never touch real products a shopper added), so a stray proxy -
        // e.g. one added from a product-recommendations row - can't nuke a legit cart. A funding cart
        // is proxy-only, so this still fully empties it. Their subtotal can also trip Gift-With-Purchase,
        // so stripping them here prevents that too.
        var updates = {};
        var found = false;
        (cart.items || []).forEach(function (i) {
          if (GBT_PROXY_HANDLES[i.handle]) { updates[i.key] = 0; found = true; }
        });
        if (!found) {
          try { sessionStorage.removeItem("gbtCartReset"); } catch (e) {}
          return null;
        }
        var already = gbtCleanupReloaded;
        try { already = already || sessionStorage.getItem("gbtCartReset"); } catch (e) {}
        return fetch("/cart/update.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ updates: updates }),
        }).then(function (res) {
          // Only reload once the removal actually succeeded - reloading on a failed/errored update
          // (proxies still present) is what could loop. res.ok + the guards make a loop impossible.
          if (!res.ok || already) return;
          gbtCleanupReloaded = true;
          try { sessionStorage.setItem("gbtCartReset", "1"); } catch (e) {}
          window.location.reload();
        });
      })
      .catch(function () {});
  }

  // Show a sticky "finish your gift" banner on storefront pages when the gifter has a PAID-but-unsent
  // gift. The cookie alone is only a HINT (it's set at send time, BEFORE payment), so a gifter who
  // backs out of the funding checkout still carries it - we must NOT nudge them. Verify against the
  // backend first: the funding order (status=pending) exists only after payment succeeds, so an
  // abandoned checkout returns 404 and we show nothing. Terminal states clear the cookie. Independent
  // of the session gate (they've usually left gift mode by now). Suppressed on the gift pages.
  function initResumeBanner() {
    var pending = readPendingCookie();
    if (!pending) return;
    var path = (window.location.pathname || "").toLowerCase();
    if (path.indexOf("/pages/gift") === 0) {
      // /pages/gift (recipient) and /pages/gift-share (gifter's send page) - stop nudging.
      clearPendingCookie();
      return;
    }
    if (!document.body || document.getElementById("gbt-resume-banner")) return;
    // Option A: skip PDPs. Product pages carry the theme's own fixed sticky add-to-cart bar at the
    // bottom, and our fixed banner would stack right on top of it. The theme tags PDPs with a
    // "template-product" body class (layout/theme.liquid). This is a gifter who already checked out,
    // so dropping the nudge on PDPs (they'll see it on the next non-product page) beats the collision.
    // Keep the cookie so the banner still shows elsewhere.
    if (/(^|\s)template-product(\s|$)/.test(document.body.className || "") ||
        path.indexOf("/products/") === 0) return;

    var api = CFG.apiBase;
    if (!api) return; // no backend configured -> never risk a false nudge

    // Only the backend can confirm a real paid, still-open gift for this token.
    fetch(api + "/storefront/v2/gift/" + encodeURIComponent(pending.t), {
      headers: { Accept: "application/json" },
    })
      .then(function (r) {
        // 404 = no funding order (abandoned checkout, OR the orders/create webhook hasn't landed yet):
        // show nothing and KEEP the cookie so a just-paid gift still nudges on the next page once the
        // webhook catches up. Any non-OK: fail closed (no banner), keep the cookie for a later retry.
        if (!r.ok) return null;
        return r.json();
      })
      .then(function (data) {
        if (!data) return;
        // status is "pending" only when paid AND within the claim window. accepted/expired/declined/etc
        // mean there's nothing to finish -> clear the cookie so we stop checking.
        if (data.status !== "pending") {
          clearPendingCookie();
          return;
        }
        renderResumeBanner(pending);
      })
      .catch(function () {}); // network error -> no banner, keep cookie
  }

  function renderResumeBanner(pending) {
    if (!document.body || document.getElementById("gbt-resume-banner")) return;

    var share =
      (CFG.sharePath || "/pages/gift-share") +
      "?token=" + encodeURIComponent(pending.t) +
      (pending.from ? "&from=" + encodeURIComponent(pending.from) : "");

    // Same green as the "Send the text" CTA on gift-by-text-share.liquid (--BTN-PRIMARY-BG-COLOR,
    // theme setting btn_primary_bg_color) - per Figma review, this banner was hardcoding a different,
    // darker green (#2f5233) that didn't match the button it leads to. Fall back to that green's
    // current value in case the CSS variable isn't defined on a page this banner can render on.
    var GBT_CTA_GREEN = "var(--BTN-PRIMARY-BG-COLOR, #567741)";

    var bar = document.createElement("div");
    bar.id = "gbt-resume-banner";
    bar.setAttribute("role", "status");
    bar.style.cssText =
      "position:fixed;left:0;right:0;bottom:0;z-index:2147483000;display:flex;align-items:center;" +
      // Extra right padding leaves clearance for the store's floating chat bubble (bottom-right) so
      // it doesn't cover the dismiss button on narrow screens.
      "justify-content:center;gap:12px;flex-wrap:wrap;padding:12px 64px 12px 16px;background:" +
      GBT_CTA_GREEN + ";color:#fff;font-size:15px;line-height:1.3;box-shadow:0 -2px 10px rgba(0,0,0,.15)";

    var msg = document.createElement("span");
    msg.textContent = "🎁 Your gift isn't sent yet — finish sending it to your recipient.";

    var go = document.createElement("a");
    go.href = share;
    go.textContent = "Finish sending →";
    go.style.cssText =
      "background:#fff;color:" + GBT_CTA_GREEN + ";font-weight:700;text-decoration:none;padding:8px 16px;" +
      "border-radius:6px;white-space:nowrap";

    var x = document.createElement("button");
    x.type = "button";
    x.setAttribute("aria-label", "Dismiss");
    x.textContent = "✕";
    x.style.cssText =
      "background:transparent;border:0;color:#fff;font-size:18px;cursor:pointer;padding:4px 8px";
    x.addEventListener("click", function () {
      clearPendingCookie();
      bar.parentNode && bar.parentNode.removeChild(bar);
    });

    bar.appendChild(msg);
    bar.appendChild(go);
    bar.appendChild(x);
    document.body.appendChild(bar);
  }

  applyGate();
  function boot() { initOptin(); watchForOptin(); cleanStrayProxies(); initResumeBanner(); }
  if (document.readyState !== "loading") boot();
  else document.addEventListener("DOMContentLoaded", boot);

  window.GiftByText = { sendByText: sendByText, _pickBouquet: pickBouquet, isEnabled: isEnabled };
})();
